/**
 * What a failed case-list read means to the agent looking at their dashboard.
 *
 * The transport (caseListApi.ts) decides *whether* a read failed and with which stable
 * code; this module decides what the user is told. The message text is the portal's own —
 * chosen from the code, never taken from whatever prose arrived over the wire (PORT-ERROR)
 * — so it is reviewable here rather than assembled at the boundary.
 *
 * A failure is a plain, serializable object rather than an `Error` subclass on purpose: it
 * rides RTK Query's `queryFn` result and is what `.unwrap()` rejects with, so it must
 * survive the store without tripping the serializability check.
 */
import type { ApiErrorCode } from './contracts';

/**
 * What the user is told, per code. Anything unrecognized falls back rather than being shown.
 */
const MESSAGE_FOR_CODE: Record<ApiErrorCode, string> = {
  InvalidRequest: 'That request could not be read. Please refresh and try again.',
  Unauthenticated: 'Your session has expired. Sign in again to continue.',
  DownstreamUnavailable: 'Your cases could not be loaded right now. Please try again shortly.',
  DownstreamRejection: 'Your cases could not be loaded. Please contact support if this continues.',
  DownstreamThrottled: 'The case service is busy right now. Please wait a moment and try again.',
  Timeout: 'Loading your cases took too long. Please try again shortly.',
  DeserializationError: 'Your cases could not be loaded. Please contact support if this continues.',
  InvalidResponse: 'Your cases could not be loaded. Please contact support if this continues.',
  ListingUnavailable: 'Case information is unavailable at this time. Please try again later.',
  UnhandledException: 'Something went wrong while loading your cases. Please try again shortly.',
  Unknown: 'Something went wrong while loading your cases. Please try again shortly.',
};

/**
 * Codes a retry cannot fix, so the view must not offer one.
 *
 * `ListingUnavailable` is a deliberate server-side refusal (the list cannot be bound to
 * the requesting agent in this environment), not a transient outage: re-issuing the same
 * request only reproduces it, so presenting "Try again" would promise a recovery that
 * cannot happen (multi-model review #4).
 */
const NON_RETRYABLE_CODES: ReadonlySet<ApiErrorCode> = new Set<ApiErrorCode>(['ListingUnavailable']);

export const FALLBACK_MESSAGE =
  'Something went wrong while loading your cases. Please try again shortly.';

/** A transport-level failure that never reached a coded API answer. */
export type FailureCode = ApiErrorCode | 'TransportFailure';

export function messageForCode(code: string | undefined): string {
  if (code !== undefined && code in MESSAGE_FOR_CODE) {
    return MESSAGE_FOR_CODE[code as ApiErrorCode];
  }

  return FALLBACK_MESSAGE;
}

/** A read that did not produce a page. */
export interface ApiFailure {
  code: FailureCode;
  /** The HTTP status the failure rode in on, or 0 when the call never completed. */
  status: number;
  /** The portal-owned message, already chosen from the code. */
  message: string;
}

/** Builds the failure for a list read that did not produce a page. */
export function listFailure(code: FailureCode, status: number): ApiFailure {
  const message = code === 'TransportFailure' ? FALLBACK_MESSAGE : messageForCode(code);
  return { code, status, message };
}

/**
 * Whether re-issuing the same read could plausibly succeed.
 *
 * A transient fault (an outage, a timeout, a dropped call) may clear on a retry, so the
 * view offers one; a deliberate refusal ({@link NON_RETRYABLE_CODES}) cannot, so it must
 * not (multi-model review #4).
 */
export function isRetryable(code: string | undefined): boolean {
  return code === undefined || !NON_RETRYABLE_CODES.has(code as ApiErrorCode);
}

/** Whether an unknown thrown value is one of our failures. */
export function isApiFailure(value: unknown): value is ApiFailure {
  return (
    typeof value === 'object' &&
    value !== null &&
    'code' in value &&
    'message' in value &&
    typeof (value as ApiFailure).message === 'string'
  );
}
